 
import { useState, useMemo } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NSButton, NSDropdown } from 'bricks';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import { IOptionAnyValue } from 'bricks/types';
import { Tooltip } from 'reactstrap';
import RenderIf from 'components/RenderIf/RenderIf';
import { PRO_FORMA_UNIT_TO_FIELD_VALUES } from 'constants/unitTypes';
import { numeralFormatterCurrency, percentFormatter } from 'ns_libs/formatter';
import {
    LOAN_RATE_TYPE, PRO_FORMA_DEVELOPMENT_DEBT_RATE_OPTIONS, SELECTED_PERIOD_CADENCE_KEY,
} from 'views/ProFormaTable/constants';
import { isDebtSource } from 'utils/sourceTypeUtils';
import {
    DevelopmentUnitOfMeasureTypes, IProFormaSourceTableRow, LoanRateType,
    IVariableLoanRatesByPeriodName, SourceCalculatedOn, IFormattedCashFlowPeriod,
} from 'views/ProFormaTable/types';
import { getLatestBudgetClassificationDate } from 'views/ProFormaTable/helpers';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import './Source.scss';
import PeriodCellValue from '../../DevelopmentUsesTable/PeriodCellValue/PeriodCellValue';

export interface ISourceProps {
    source: IProFormaSourceTableRow;
    totalSourcesAmount: number;
    unitType: DevelopmentUnitOfMeasureTypes;
    selectedMetric: keyof IProFormaSourceTableRow | null;
    hasWriteAccessToProForma: boolean;
    projectionPeriods: IFormattedCashFlowPeriod[];
}

const Source = ({
    source, totalSourcesAmount, unitType, selectedMetric, hasWriteAccessToProForma, projectionPeriods,
}: ISourceProps) => {
    const {
        handledDeleteSource, isSourceDeletionLoading, handleUpdateSource, sourceTypes,
        showCents, periodCadence, getLocalStorageValueByKey, proForma, uses,
    } = useProFormaSocketContext();
    const [isHovered, setIsHovered] = useState(false);
    const [showVariableLoanRateTooltip, setShowVariableLoanRateTooltip] = useState(false);

    const sourceType = sourceTypes?.find(sourceType => sourceType.correlationId === source.sourceTypeId);
    const isDebtSourceType = isDebtSource(source.sourceTypeId, sourceTypes);

    const localStorageCadence = getLocalStorageValueByKey(SELECTED_PERIOD_CADENCE_KEY)
        ? getLocalStorageValueByKey(SELECTED_PERIOD_CADENCE_KEY) : Periods.MONTHLY;

    const toggleVariableLoanRateTooltip = () => setShowVariableLoanRateTooltip(!showVariableLoanRateTooltip);

    const traunchOptions: IOptionAnyValue[] = Array.from({ length: totalSourcesAmount }, (_, index) => ({
        label: `${index + 1}`,
        value: index + 1,
    }));

    const updateMetricAmount = (updatedValue: number) => {
        const updatedMetricAmount = Math.min(Number(updatedValue), 1);
        if (selectedMetric) {
            handleUpdateSource(source.id, {
                [selectedMetric]: updatedMetricAmount,
                calculatedOn: 'metric_percentage' as SourceCalculatedOn,
            });
        }
    };

    const updateFixedLoanRate = (updatedValue: number) => {
        const updatedFixedLoanRate = Math.min(Number(updatedValue), 1);
        if (source.loanRateType === LOAN_RATE_TYPE.FIXED) {
            handleUpdateSource(source.id, {
                loanRateType: LOAN_RATE_TYPE.FIXED as LoanRateType,
                fixedLoanRate: updatedFixedLoanRate,
            });
        }
    };

    const updateVariableLoanRate = (updatedValue: string, cadenceIndex: number, periodIndex: number) => {
        const sanitizedValue = Math.max(Number(updatedValue), 0).toString();

        if (source.variableLoanRates) {
            const updatedPeriods = [...source.variableLoanRates.variableLoanRates[cadenceIndex].periods];
            updatedPeriods.splice(periodIndex, 1, {
                ...source.variableLoanRates.variableLoanRates[cadenceIndex].periods[periodIndex],
                loanRate: sanitizedValue,
            });
            const updatedCadences = [...source.variableLoanRates.variableLoanRates];
            updatedCadences.splice(cadenceIndex, 1, {
                ...source.variableLoanRates.variableLoanRates[cadenceIndex],
                periods: updatedPeriods,
            });
            const updatedVariableLoanRates = {
                ...source.variableLoanRates,
                variableLoanRates: updatedCadences,
            };
            handleUpdateSource(source.id, {
                variableLoanRates: updatedVariableLoanRates,
            });
        }
    };

    const sourceUnitAmount = source[PRO_FORMA_UNIT_TO_FIELD_VALUES[unitType]] !== null ? source[PRO_FORMA_UNIT_TO_FIELD_VALUES[unitType]] : null;

    const variableLoanRatesByPeriodName: IVariableLoanRatesByPeriodName = useMemo(() => {
        const loanRatesByPeriodName: IVariableLoanRatesByPeriodName = {};
        source.variableLoanRates?.variableLoanRates.forEach((cadence, cadenceIndex) => {
            cadence.periods.forEach((period, periodIndex) => {
                loanRatesByPeriodName[period.periodName] = {
                    cadenceIndex,
                    periodIndex,
                    value: period.loanRate,
                };
            });
        });
        return loanRatesByPeriodName;
    }, [source.variableLoanRates?.variableLoanRates]);

    // Determine if variable loan rate type is allowed based on the pro forma start date and budget classification date
    const allowVariableLoanRateType = !!proForma.startDate
        && (!!getLatestBudgetClassificationDate(uses.uses) || !!source.variableLoanRates?.variableLoanRates);
    const loanRateTypes = allowVariableLoanRateType
        ? PRO_FORMA_DEVELOPMENT_DEBT_RATE_OPTIONS
        : PRO_FORMA_DEVELOPMENT_DEBT_RATE_OPTIONS.filter(option => option.value !== LOAN_RATE_TYPE.VARIABLE);

    return (
        <tr className="NSTable__tbody__tr Source Source__row">
            <td
                className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border align-middle
                    NSTable__tbody__tr__td__input"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="d-flex justify-content-between">
                    <div className="mt-2">
                        <div className="d-flex">
                            <div className="ml-1">{sourceType?.name}</div>
                            <RenderIf isTrue={isDebtSourceType}>
                                <NSDropdown
                                    options={loanRateTypes}
                                    selectedOption={loanRateTypes.find(option => option.value === source.loanRateType)}
                                    handleSelectOption={option => {
                                        if (source.loanRateType !== option.value) {
                                            handleUpdateSource(source.id, { loanRateType: option.value as LoanRateType, fixedLoanRate: null });
                                        }
                                    }}
                                    isBadgeToggle
                                    badgeColor="none"
                                    containerClassName="text-dark"
                                    useBodyContainer
                                />
                            </RenderIf>

                            <RenderIf isTrue={localStorageCadence !== Periods.MONTHLY
                                && source.loanRateType === PRO_FORMA_DEVELOPMENT_DEBT_RATE_OPTIONS[1].value}
                            >
                                <FontAwesomeIcon
                                    id="variable-loan-rate-tooltip"
                                    icon={faCircleInfo}
                                    className="my-auto text-dark"
                                />
                                <Tooltip
                                    target="variable-loan-rate-tooltip"
                                    isOpen={showVariableLoanRateTooltip}
                                    toggle={toggleVariableLoanRateTooltip}
                                >
                                    {`When viewing periods, the interest rate is calculated as the effective rate. 
                                    To edit variable rates, switch to viewing monthly periods.`}
                                </Tooltip>
                            </RenderIf>
                        </div>
                        <div className="d-flex Source__row__cell-input--height-45">
                            <NSCellInput
                                value={source.name}
                                handleUpdateValue={updatedValue => {
                                    handleUpdateSource(source.id, { name: updatedValue as string });
                                }}
                                placeholder="Enter source name"
                                labelClassName="my-1"
                            />

                            <RenderIf isTrue={source.loanRateType === LOAN_RATE_TYPE.FIXED}>
                                <NSCellInput
                                    type="number"
                                    numberType="percentage"
                                    value={source.fixedLoanRate}
                                    handleUpdateValue={updatedValue => updateFixedLoanRate(updatedValue as number)}
                                    placeholder="Enter percentage"
                                    labelClassName="my-1"
                                />
                            </RenderIf>
                        </div>
                    </div>

                    <RenderIf isTrue={isHovered && hasWriteAccessToProForma}>
                        <div className="d-flex align-items-center">
                            <RenderIf isTrue={isSourceDeletionLoading}>
                                <span className="spinner-border spinner-border-sm text-muted mr-2 ml-1" />
                            </RenderIf>
                            <RenderIf isTrue={!isSourceDeletionLoading}>
                                <NSButton
                                    icon={faTrash}
                                    size="sm"
                                    className={classNames('px-2 mr-2 text-muted hover:text-dark bg-none hover:bg-light')}
                                    callback={() => handledDeleteSource(source.id)}
                                />
                            </RenderIf>
                        </div>
                    </RenderIf>
                </div>
            </td>
            <td className="NSTable__tbody__tr__td align-middle NSTable__tbody__tr__td--width-100">
                <RenderIf isTrue={isDebtSourceType && selectedMetric}>
                    <div className="d-flex justify-content-end text-primary">
                        <NSCellInput
                            type="number"
                            numberType="percentage"
                            value={selectedMetric && source[selectedMetric] !== null ? Number(source[selectedMetric]) : null}
                            handleUpdateValue={updatedValue => updateMetricAmount(updatedValue as number)}
                            placeholder="Enter percentage"
                            isRightAligned
                        />
                    </div>
                </RenderIf>
            </td>
            <td className="NSTable__tbody__tr__td align-middle">
                <div className="d-flex justify-content-end">
                    <NSDropdown
                        options={traunchOptions}
                        selectedOption={traunchOptions?.find(traunch => traunch.value === source.traunch)}
                        handleSelectOption={option => (source.traunch !== option.value ? handleUpdateSource(source.id, { traunch: option.value }) : null)}
                        isBadgeToggle
                        badgeColor="none"
                        containerClassName="text-primary"
                        useBodyContainer
                    />
                </div>
            </td>
            <td className="NSTable__tbody__tr__td align-middle">
                <div className="d-flex justify-content-end text-dark">{percentFormatter((source.sourcesPercentage || 0) * 100)}</div>
            </td>
            <td className="NSTable__tbody__tr__td align-middle">
                <div className="d-flex justify-content-end text-dark">
                    {sourceUnitAmount !== null ? numeralFormatterCurrency(Number(sourceUnitAmount)) : '—'}
                </div>
            </td>
            <td className="NSTable__tbody__tr__td align-middle NSTable__tbody__tr__td--width-100">
                <div className="d-flex align-items-center justify-content-end">
                    <NSCellInput
                        type="number"
                        numberType="currency"
                        value={source.amount}
                        handleUpdateValue={updatedValue => {
                            if (source.metricType) {
                                handleUpdateSource(source.id, {
                                    amount: Math.max(updatedValue as number, 0),
                                    calculatedOn: 'amount' as SourceCalculatedOn,
                                });
                            } else {
                                handleUpdateSource(source.id, { amount: Math.max(updatedValue as number, 0) as number });
                            }
                        }}
                        placeholder="Enter amount"
                        isRightAligned
                        showCents={showCents}
                    />
                </div>
            </td>
            {projectionPeriods?.map((period, index) => {
                let periodsTotals = source.periods || [];
                if (periodCadence === Periods.ANNUALLY) periodsTotals = source.yearlyPeriods || [];
                const targetPeriod = periodsTotals[index];

                return (
                    <PeriodCellValue
                        key={`source-period-cell-${period.value}`}
                        tooltipId={`source-period-cell-${index}`}
                        period={period}
                        value={targetPeriod?.amountToFund === null || source.amount === null
                            ? '—' : numeralFormatterCurrency(targetPeriod?.amountToFund, showCents ? '$0,0.00' : '$0,0')}
                        cellClassName={classNames(['NSTable__tbody__tr__td text-dark text-right', {
                            'pt-1': source.loanRateType === LOAN_RATE_TYPE.VARIABLE,
                        }])}
                        overlayingCellInput={source.loanRateType === LOAN_RATE_TYPE.VARIABLE && localStorageCadence === Periods.MONTHLY ? (
                            <div
                                className={classNames(['d-flex align-items-center justify-content-end', {
                                    invisible: !variableLoanRatesByPeriodName[period.label],
                                }])}
                            >
                                <NSCellInput
                                    isRightAligned
                                    type="number"
                                    numberType="percentage"
                                    value={variableLoanRatesByPeriodName[period.label]?.value}
                                    handleUpdateValue={updatedValue => updateVariableLoanRate(
                                        String(updatedValue),
                                        variableLoanRatesByPeriodName[period.label]?.cadenceIndex,
                                        variableLoanRatesByPeriodName[period.label]?.periodIndex,
                                    )}
                                    inputClassName="my-1"
                                    placeholder="Enter %"
                                    labelClassName="my-2"
                                    inputHeight="33px"
                                    disabled={!variableLoanRatesByPeriodName[period.label]}
                                />
                            </div>
                        ) : null}
                    />
                );
            })}
        </tr>
    );
};

export default Source;
